import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Badge, Checkbox } from '@aimsos/ui';

/** Column type → DS badge variant. */
const TYPE_VARIANT = { text: 'secondary', number: 'informative', date: 'purple', boolean: 'neutral' };

/**
 * One column row in the picker (CT-5195).
 *
 * Shows all four attributes — display name, type, description and the internal
 * key — because the key is what a user cross-references against Data Studio and
 * the description is often the only way to tell two similar fields apart.
 *
 * The display name and description are **service data**: they are rendered as-is
 * and never passed through `t()`, so they stay in their source language when the
 * UI switches to Spanish.
 */
const ColumnPickerRow = memo(function ColumnPickerRow({
  column, typeLabel, checked, onToggle, disabledReason,
}) {
  // A mapped field the backend cannot take yet (CT-5480): visible, never toggled.
  const disabled = Boolean(disabledReason);
  // `fieldUuid`, not `internalName`: this is the identity that ends up in
  // `dataset.exposedColumns` and, from there, straight into the create/update
  // payload's `fieldUuid` — the code is shown below only for the user to
  // cross-reference against Data Studio.
  const handleToggle = useCallback(() => {
    if (!disabled) onToggle(column.fieldUuid);
  }, [column.fieldUuid, disabled, onToggle]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        if (!disabled) onToggle(column.fieldUuid);
      }
    },
    [column.fieldUuid, disabled, onToggle],
  );

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-label={column.displayName}
      title={disabledReason}
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={[
        'flex min-w-0 items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        !disabled && 'cursor-pointer',
        disabled && 'cursor-not-allowed opacity-50',
        !disabled && checked && 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/30',
        !disabled && !checked && 'border-edge hover:border-brand-300',
        disabled && 'border-edge',
      ].filter(Boolean).join(' ')}
    >
      {/*
        The DS `Checkbox` is itself `role="checkbox"` **and** focusable
        (`tabIndex={0}`), so nesting it inside this row would expose two
        controls and put a Tab stop on the one screen readers cannot announce.
        `inert` takes the whole visual indicator out of both the a11y tree and
        the tab order in one attribute — `aria-hidden` alone would leave the
        focus trap. Passing `disabled` instead would fix the tab order but dim
        every row.
      */}
      <span className="pointer-events-none mt-0.5 shrink-0" inert>
        <Checkbox size="small" checked={checked} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-content">{column.displayName}</span>
          <Badge size="small" variant={TYPE_VARIANT[column.type] ?? 'neutral'}>
            {typeLabel}
          </Badge>
          <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-content-muted">
            {column.internalName}
          </code>
          {/* Where a composite's mapped field comes from (CT-5480): service
              data, so not translated. */}
          {column.origin?.table ? (
            <span className="truncate text-[11px] text-content-muted">· {column.origin.table}</span>
          ) : null}
        </div>
        {disabledReason ? (
          <p className="m-0 text-xs text-content-muted">{disabledReason}</p>
        ) : null}
        {column.description ? (
          <p className="m-0 text-xs text-content-muted">{column.description}</p>
        ) : null}
      </div>
    </div>
  );
});

ColumnPickerRow.propTypes = {
  column: PropTypes.shape({
    fieldUuid: PropTypes.string.isRequired,
    internalName: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    origin: PropTypes.shape({ table: PropTypes.string, role: PropTypes.string }),
  }).isRequired,
  typeLabel: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  /** Set when the field cannot be picked yet; shown under it and as its tooltip. */
  disabledReason: PropTypes.string,
};

export default ColumnPickerRow;
